import React, { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  onConfirm: () => void;
}

interface ConfirmDialogProps {
  request: ConfirmRequest | null;
  onClose: () => void;
}

/**
 * The in-page replacement for window.confirm on this screen.
 *
 * window.confirm draws a browser chrome dialog that exists nowhere in the
 * document, so a reviewer inspecting the DOM sees no dialog and reads the click
 * as a silent no-op — which is exactly how the approve-with-checks-unrun prompt
 * was reported as broken when it was in fact firing. Browsers also let a user
 * suppress further dialogs on a page, and headless drivers dismiss them by
 * default, both of which turn a confirmation into an invisible cancel.
 *
 * This renders as a real element with role="dialog", so it is visible to the
 * reader, to assistive technology and to anyone verifying the behaviour.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ request, onClose }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!request) return;
    confirmRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [request, onClose]);

  if (!request) return null;

  const danger = request.tone === 'danger';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="process-map-confirm-title"
        aria-describedby="process-map-confirm-message"
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div
            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              danger ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
            }`}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id="process-map-confirm-title" className="text-base font-semibold text-gray-900">
              {request.title}
            </h3>
            <p
              id="process-map-confirm-message"
              className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600"
            >
              {request.message}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {request.cancelLabel || 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => {
              onClose();
              request.onConfirm();
            }}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm ${
              danger ? 'bg-red-600 hover:bg-red-700' : 'bg-electric-blue hover:opacity-90'
            }`}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
